import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Plus, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

import BookingsTab from "../components/dashboard/BookingsTab";
import ProfileTab from "../components/dashboard/ProfileTab";
import { ListingsTab, EnquiriesTab, PaymentsTab, ComplaintsTab, SavedTab } from "../components/dashboard/SimpleTabs";
import { LifecycleTab, ZoloTab } from "../components/dashboard/LifecycleTabs";
import OwnerVendorsTab from "../components/dashboard/OwnerVendorsTab";
import PropertyAnalyticsTab from "../components/dashboard/PropertyAnalyticsTab";

export default function Dashboard() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [saved, setSaved] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [myProps, setMyProps] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [ownerStats, setOwnerStats] = useState(null);

  const [profile, setProfile] = useState({ name:"", phone:"", city:"", college:"", avatar:"", business_name:"", gst_number:"" });
  const [kycDocs, setKycDocs] = useState([]);

  const loadAll = async () => {
    if (!user || typeof user !== "object") return;
    api.get("/enquiries").then(r => setEnquiries(r.data)).catch(()=>{});
    api.get("/bookings").then(r => setBookings(r.data)).catch(()=>{});
    api.get("/complaints").then(r => setComplaints(r.data)).catch(()=>{});
    api.get("/payments/my").then(r => setPayments(r.data)).catch(()=>{});
    if (user.role === "student") {
      api.get("/saved").then(r => setSaved(r.data)).catch(()=>{});
    }
    if (user.role === "owner" || user.role === "admin") {
      api.get("/properties", { params: { owner_id: user.id, status: "all" } }).then(r => setMyProps(r.data)).catch(()=>{});
      if (user.role === "owner") api.get("/owner/analytics").then(r => setOwnerStats(r.data)).catch(()=>{});
    }
    setProfile({
      name: user.name||"", phone: user.phone||"", city: user.city||"", college: user.college||"",
      avatar: user.avatar||"", business_name: user.business_name||"", gst_number: user.gst_number||"",
    });
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [user]);

  const updateEnquiryStatus = async (eid, status) => {
    try {
      await api.patch(`/enquiries/${eid}`, null, { params: { status } });
      setEnquiries(enquiries.map(e => e.id === eid ? { ...e, status } : e));
      toast.success("Enquiry updated");
    } catch { toast.error("Failed"); }
  };

  const updateBookingStatus = async (bid, status) => {
    try {
      await api.patch(`/bookings/${bid}/status`, null, { params: { status } });
      setBookings(bookings.map(b => b.id === bid ? { ...b, status } : b));
      toast.success(`Booking ${status}`);
    } catch { toast.error("Failed"); }
  };

  const refundBooking = async (bid) => {
    const reason = prompt("Refund reason (optional)?", "Issued by " + (user?.role || "owner"));
    if (reason === null) return;
    try {
      const { data } = await api.post("/payments/refund", { booking_id: bid, reason });
      if (data.ok) toast.success("Refund initiated");
      else toast.error("Refund failed: " + (data.error || "provider error"));
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refund failed"); }
  };

  const payNow = async (booking) => {
    try {
      let rzpCfg = { enabled: false };
      try { rzpCfg = (await api.get("/payments/razorpay/config")).data; } catch { /* ignore */ }

      if (rzpCfg.enabled && typeof window.Razorpay !== "undefined") {
        const { data: order } = await api.post("/payments/razorpay/order", { booking_id: booking.id });
        const rzp = new window.Razorpay({
          key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
          name: "Hostro", description: order.property_title, prefill: order.prefill, theme: { color: "#2ED573" },
          handler: async (response) => {
            try {
              await api.post("/payments/razorpay/verify", {
                booking_id: booking.id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              toast.success("Payment confirmed!");
              loadAll();
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed"); }
          },
        });
        rzp.open();
        return;
      }
      const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const saveProfile = async () => {
    try {
      await api.put("/auth/profile", { ...profile, kyc_documents: kycDocs.length ? kycDocs : undefined });
      toast.success("Profile updated");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-5 py-20">Loading...</div>;
  if (!user || typeof user !== "object") return (
    <div className="max-w-3xl mx-auto px-5 py-20 text-center">
      <h1 className="font-display font-black text-4xl">Sign in to view your dashboard.</h1>
      <Link to="/login" className="brut-btn mt-6 inline-flex">Sign in</Link>
    </div>
  );

  const tabs = [
    { id: "overview", label: "Overview" },
    ...(user.role === "owner" || user.role === "admin" ? [{ id: "listings", label: "My Listings" }, { id: "analytics", label: "Analytics" }] : []),
    { id: "bookings", label: user.role === "owner" ? "Customer Bookings" : "My Bookings" },
    { id: "lifecycle", label: "Agreement & Rent" },
    { id: "enquiries", label: user.role === "owner" ? "Leads" : "Enquiries" },
    { id: "payments", label: "Payments" },
    { id: "complaints", label: "Complaints" },
    ...(user.role === "owner" || user.role === "admin" ? [{ id: "vendors", label: "Vendors" }] : []),
    ...(user.role === "student" ? [{ id: "zolo", label: "Food & Services" }, { id: "saved", label: "Saved" }] : []),
    { id: "profile", label: "Profile / KYC" },
  ];

  const totalRevenue = bookings.filter(b=>b.payment_status==="paid").reduce((s,b)=>s+(b.total_amount||0),0);
  const openComplaintsCount = complaints.filter(c => c.status === "open").length;

  return (
    <div className="panel-shell" data-testid="dashboard-shell">
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-10" data-testid="dashboard-page">
      <div className="brut-card p-6 md:p-8 flex flex-wrap items-center justify-between gap-4 bg-[#ECFAF1]">
        <div className="flex items-center gap-4">
          {user.avatar
            ? <img src={user.avatar} alt="" className="w-16 h-16 brut-border object-cover"/>
            : <div className="w-16 h-16 brut-border bg-[#2ED573] flex items-center justify-center font-display font-black text-2xl">{user.name?.[0]}</div>}
          <div>
            <span className="brut-tag pink uppercase">{user.role}{user.verified && " · verified"}</span>
            <h1 className="font-display font-black text-3xl md:text-4xl mt-2">Hey, {user.name?.split(" ")[0]} 👋</h1>
            <p className="text-[#4A4A4A] mt-1 flex flex-wrap items-center gap-3 text-sm">
              <span className="flex items-center gap-1"><Mail size={14}/>{user.email}</span>
              {user.phone && <span className="flex items-center gap-1"><Phone size={14}/>{user.phone}</span>}
            </p>
          </div>
        </div>
        {user.role === "owner" && (
          <Link to="/list-property" className="brut-btn" data-testid="add-property-btn"><Plus size={16}/> Add property</Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-6" data-testid="dashboard-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)} className={`brut-tag ${tab===t.id ? "lime" : "white"} cursor-pointer`} data-testid={`tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "overview" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="overview-grid">
              <div className="brut-card p-5"><div className="brut-tag lime">Bookings</div><div className="font-display font-black text-4xl mt-2">{bookings.length}</div></div>
              <div className="brut-card p-5"><div className="brut-tag pink">Enquiries</div><div className="font-display font-black text-4xl mt-2">{enquiries.length}</div></div>
              {(user.role === "owner" || user.role === "admin") ? (
                <>
                  <div className="brut-card p-5"><div className="brut-tag blue">Listings</div><div className="font-display font-black text-4xl mt-2">{myProps.length}</div></div>
                  <div className="brut-card p-5 bg-[#2ED573]"><div className="brut-tag white">Revenue (₹)</div><div className="font-display font-black text-3xl mt-2">{totalRevenue.toLocaleString()}</div></div>
                </>
              ) : (
                <>
                  <div className="brut-card p-5"><div className="brut-tag blue">Saved</div><div className="font-display font-black text-4xl mt-2">{saved.length}</div></div>
                  <div className="brut-card p-5"><div className="brut-tag white">Open issues</div><div className="font-display font-black text-4xl mt-2">{openComplaintsCount}</div></div>
                </>
              )}
            </div>

            {user.role === "owner" && ownerStats?.monthly_revenue?.length > 0 && (
              <div className="brut-card p-5 mt-6">
                <h3 className="font-display font-black text-2xl mb-3">Monthly revenue</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={ownerStats.monthly_revenue}>
                    <CartesianGrid stroke="#0F1F17" strokeDasharray="3 3" opacity={0.2}/>
                    <XAxis dataKey="month"/><YAxis/>
                    <Tooltip contentStyle={{border:"2px solid #0F1F17", borderRadius:0}}/>
                    <Line type="monotone" dataKey="revenue" stroke="#0F1F17" strokeWidth={3} dot={{r:5, fill:"#2ED573", stroke:"#0F1F17", strokeWidth:2}}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}

        {tab === "listings" && <ListingsTab myProps={myProps}/>}
        {tab === "analytics" && <PropertyAnalyticsTab/>}
        {tab === "bookings" && <BookingsTab bookings={bookings} user={user} payNow={payNow} updateBookingStatus={updateBookingStatus} refundBooking={refundBooking}/>}
        {tab === "lifecycle" && <LifecycleTab bookings={bookings} user={user}/>}
        {tab === "zolo" && <ZoloTab bookings={bookings} user={user}/>}
        {tab === "vendors" && <OwnerVendorsTab user={user}/>}
        {tab === "enquiries" && <EnquiriesTab enquiries={enquiries} user={user} updateEnquiryStatus={updateEnquiryStatus}/>}
        {tab === "payments" && <PaymentsTab payments={payments}/>}
        {tab === "complaints" && <ComplaintsTab complaints={complaints}/>}
        {tab === "saved" && <SavedTab saved={saved}/>}
        {tab === "profile" && <ProfileTab user={user} profile={profile} setProfile={setProfile} kycDocs={kycDocs} setKycDocs={setKycDocs} saveProfile={saveProfile}/>}
      </div>
    </div>
    </div>
  );
}
